/* eslint-disable @typescript-eslint/no-empty-function */
import React, {
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Dayjs, dayjs } from "../../../lib/dayjs";
import { changePeriod, filterUniqueItems } from "../../../utils";
import { updateDateRange } from "../../../utils/dateRangeUtils";
import useFiltersLocalStorage from "./useFiltersLocalStorage";
import { IS_DEMO } from "../../../config/constants";

export type ChartLegendType = {
  label: string;
  color: string;
  category: string[];
  description: string;
  isDashed?: boolean;
  tooltipText?: string;
  reference: string;
};

export type ChartContext = {
  chartId: string;
  selectedChartVariables: string[];
  setSelectedChartVariables: (selectedChartVariables: string[]) => void;
  handleLegendClick: (sensor: string) => void;
  period: string;
  defaultPeriod: string;
  changeDateRange: ({
    dateRange,
    period,
    isZoom,
  }: {
    dateRange?: { start: Dayjs; end: Dayjs | null };
    period?: string;
    isZoom?: boolean;
  }) => void;
  dateRange: { start: Dayjs; end: Dayjs };
  excludeGap: boolean;
  setExcludeGap: (excludeGap: boolean) => void;
  chartLegends: ChartLegendType[];
  setChartLegends: (chartLegends: ChartLegendType[]) => void;
  legendCategory: string;
  setLegendCategory: (legendCategory: string) => void;
  isFlaring?: boolean;
  printRef?: React.RefObject<HTMLDivElement>;
};

export const DEFAULT_CHART_PERIOD = String(30 * 24);
const getDateRange = (period: string): { start: Dayjs; end: Dayjs } => {
  return {
    start: dayjs().subtract(+period, "hours"),
    end: dayjs(),
  };
};

const initialState: ChartContext = {
  period: DEFAULT_CHART_PERIOD,
  defaultPeriod: DEFAULT_CHART_PERIOD,
  selectedChartVariables: [],
  setSelectedChartVariables: () => {},
  handleLegendClick: () => {},
  excludeGap: false,
  changeDateRange: () => {},
  dateRange: getDateRange(DEFAULT_CHART_PERIOD),
  setExcludeGap: () => {},
  chartLegends: [],
  setChartLegends: () => {},
  chartId: "",
  legendCategory: "ALL",
  setLegendCategory: () => {},
  isFlaring: false,
};

export const ChartContext = createContext(initialState);

export const ChartContextProvider: React.FC<{
  children: ReactNode;
  defaultPeriod?: string;
  preSelectedChartVariables?: string[];
  alarmDateRange?: { start: Dayjs; end: Dayjs };
  chartId: string;
  isFlaring?: boolean;
  className?: string;
}> = ({
  children,
  defaultPeriod = DEFAULT_CHART_PERIOD,
  preSelectedChartVariables,
  alarmDateRange,
  chartId,
  isFlaring = false,
  className = "",
}) => {
  const { saveOnLocalStorage, getSavedFilters } =
    useFiltersLocalStorage(chartId);
  const [chartLegends, setChartLegends] = useState<ChartLegendType[]>([]);
  const printRef = useRef<HTMLDivElement>(null);
  const [legendCategory, setLegendCategory] = useState<string>("ALL");
  const [selectedChartVariables, setSelectedChartVariables] = useState<
    string[]
  >(preSelectedChartVariables ?? []);
  const [period, setPeriod] = useState<string>(
    getSavedFilters(chartId).period ?? defaultPeriod
  );

  const [dateRange, setDateRange] = useState<{ start: Dayjs; end: Dayjs }>(
    alarmDateRange ? changePeriod(alarmDateRange, period) : getDateRange(period)
  );

  const [excludeGap, setExcludeGap] = useState<boolean>(false);

  useEffect(() => {
    if (preSelectedChartVariables) {
      const tags = getSavedFilters(chartId).tags ?? preSelectedChartVariables;

      setSelectedChartVariables(tags);
    }
  }, [preSelectedChartVariables]);

  const changeLegendCategory = (
    category: string,
    newChartLegends?: ChartLegendType[]
  ) => {
    setLegendCategory(category);
    const saved = getSavedFilters(chartId);
    const defaultTags =
      saved.category === category
        ? saved.tags ?? []
        : preSelectedChartVariables ?? [];

    if (defaultTags.length > 0) {
      setSelectedChartVariables(defaultTags);
    } else {
      const shouldHaveInitialSensor = !chartId.includes("monitoring");

      if (shouldHaveInitialSensor) {
        const haveLegendFilter = category && category !== "ALL";
        const filteredTags = newChartLegends ?? chartLegends;

        const initialSensor = haveLegendFilter
          ? filteredTags.filter((tag) => tag.category.includes(category))
          : filteredTags;

        initialSensor.sort((a, b) => a.label.localeCompare(b.label));
        setSelectedChartVariables(
          initialSensor.map((tag) => tag.reference).slice(0, 10)
        );
      }
    }

    const tags = newChartLegends ?? chartLegends;
    if (
      ((newChartLegends && newChartLegends?.length > 0) ||
        (!newChartLegends && chartLegends.length > 0)) &&
      tags.some((legend) => legend.category.includes(category))
    ) {
      saveOnLocalStorage({ category }, chartId);
    }
  };

  const changeChartLegends = (newChartLegends: ChartLegendType[]) => {
    const defaultLegendCategory = newChartLegends.some((legend) =>
      legend.category.includes("Sensors - Alarm")
    )
      ? "Sensors - Alarm"
      : "ALL";
    setChartLegends(newChartLegends);
    const savedCategory = getSavedFilters(chartId).category;
    const category =
      savedCategory && savedCategory !== "ALL"
        ? savedCategory
        : defaultLegendCategory;

    changeLegendCategory(category, newChartLegends);
  };

  const changeDateRange = ({
    dateRange,
    period,
    isZoom = false,
  }: {
    dateRange?: { start: Dayjs; end: Dayjs | null };
    period?: string;
    isZoom?: boolean;
  }) => {
    const fixDateRange =
      alarmDateRange && period
        ? changePeriod(alarmDateRange, period)
        : getDateRange(period ?? defaultPeriod);
    console.log(fixDateRange, dateRange);
    updateDateRange({
      dateRange: dateRange ?? fixDateRange,
      period,
      isZoom,
      chartId,
      saveOnLocalStorage,
      alarmDateRange,
      defaultPeriod,
      setDateRange,
      setPeriod,
      isFlaring,
    });
  };

  useEffect(() => {
    const { period, dateRange } = getSavedFilters(chartId);

    changeDateRange({
      period: period ?? defaultPeriod,
      dateRange: IS_DEMO ? undefined : dateRange,
    });
  }, [chartId]);

  useEffect(() => {
    const newPeriod = getSavedFilters(chartId).period ?? defaultPeriod;
    if (
      alarmDateRange &&
      newPeriod === "alarm" &&
      !getSavedFilters(chartId).zoomInterval
    ) {
      changeDateRange({ period: newPeriod });
    }
  }, [alarmDateRange]);

  const handleLegendClick = (sensor: string) => {
    if (filterUniqueItems(selectedChartVariables, sensor).length === 0) {
      return;
    }
    const newTags = filterUniqueItems(selectedChartVariables, sensor);

    saveOnLocalStorage(
      {
        tags: newTags,
        category: legendCategory,
      },
      chartId
    );

    setSelectedChartVariables(newTags);
  };

  const context = useMemo(
    () => ({
      chartId,
      period,
      defaultPeriod,
      selectedChartVariables,
      setSelectedChartVariables: (selectedChartVariables: string[]) => {
        saveOnLocalStorage(
          {
            tags: selectedChartVariables,
            category: legendCategory,
          },
          chartId
        );
        setSelectedChartVariables(selectedChartVariables);
      },
      handleLegendClick,
      changeDateRange,
      dateRange,
      excludeGap,
      setExcludeGap,
      chartLegends,
      setChartLegends: changeChartLegends,
      legendCategory,
      setLegendCategory: changeLegendCategory,
      printRef,
    }),
    [
      chartId,
      period,
      defaultPeriod,
      selectedChartVariables,
      handleLegendClick,
      setSelectedChartVariables,
      changeDateRange,
      dateRange,
      excludeGap,
      setExcludeGap,
      chartLegends,
      setChartLegends,
      legendCategory,
      setLegendCategory,
      printRef,
    ]
  );

  return (
    <ChartContext.Provider key={chartId} value={context}>
      <div className={`chart-wrapper ${className}`} ref={printRef}>
        {children}
      </div>
    </ChartContext.Provider>
  );
};
export const useChartVariables = () => {
  return useContext(ChartContext);
};
